import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import PasswordStrengthMeter from './PasswordStrengthMeter';
import { supabase } from '../config/supabase';
import toast from 'react-hot-toast';

const { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight, FiLoader, FiCheckCircle } = FiIcons;
const { FaGoogle, FaGithub, FaApple } = FaIcons;

export default function Registration({ onSwitchToLogin, onSuccess }) {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid = emailRegex.test(formData.email);
    const isPasswordValid = formData.password.length >= 10;
    
    if (formData.email && !isEmailValid) {
      setEmailError('Please enter a valid email address.');
    } else {
      setEmailError('');
    }

    setIsFormValid(isEmailValid && isPasswordValid);
  }, [formData]);

  const handleSocialRegister = (provider) => {
    toast.error(`${provider} integration requires production redirect URLs.`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: window.location.origin,
        }
      });

      if (error) {
        if (error.message.includes('already registered')) {
          toast.error('Account already exists. Switching to login...');
          setTimeout(onSwitchToLogin, 2000);
        } else {
          throw error;
        }
      } else {
        toast.success('Verification email sent! Please check your inbox.');
        onSuccess?.();
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl border border-gray-100 overflow-hidden"
    >
      <div className="p-8 md:p-10">
        <header className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-blue-100 rotate-3">
            <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-black text-gray-900 tracking-tighter uppercase">Join ClipShares</h2>
          <p className="text-gray-400 font-bold text-xs uppercase tracking-widest mt-2">Create your secure account</p>
        </header>

        {/* Primary Option: Social Logins */}
        <div className="space-y-3 mb-8">
          <SocialButton 
            icon={FaGoogle} 
            label="Continue with Google" 
            onClick={() => handleSocialRegister('Google')}
            color="hover:bg-red-50 hover:text-red-600 hover:border-red-100"
          />
          <div className="grid grid-cols-2 gap-3">
            <SocialButton 
              icon={FaGithub} 
              label="GitHub" 
              onClick={() => handleSocialRegister('GitHub')}
              color="hover:bg-gray-900 hover:text-white"
            />
            <SocialButton 
              icon={FaApple} 
              label="Apple" 
              onClick={() => handleSocialRegister('Apple')}
              color="hover:bg-black hover:text-white"
            />
          </div>
        </div>

        <div className="relative mb-8">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100"></div></div>
          <div className="relative flex justify-center text-[10px] uppercase font-black tracking-widest">
            <span className="bg-white px-4 text-gray-300">Or use email</span>
          </div>
        </div>

        {/* Secondary Option: Email/Password */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Email Address</label>
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-600 transition-colors">
                <SafeIcon icon={FiMail} className="w-5 h-5" />
              </div>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full pl-12 pr-4 py-4 bg-gray-50 border-2 rounded-2xl font-medium outline-none transition-all ${
                  emailError ? 'border-red-500 bg-red-50' : 'border-transparent focus:border-blue-500 focus:bg-white'
                }`}
                placeholder="name@domain.com"
              />
            </div>
            {emailError && (
              <motion.p initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="text-[10px] text-red-500 font-black uppercase mt-2 ml-1">
                {emailError}
              </motion.p>
            )}
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Secure Password</label>
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-600 transition-colors">
                <SafeIcon icon={FiLock} className="w-5 h-5" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full pl-12 pr-12 py-4 bg-gray-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-2xl font-medium outline-none transition-all"
                placeholder="Min. 10 characters"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
              </button>
            </div>
            <PasswordStrengthMeter password={formData.password} />
          </div>

          <motion.button
            type="submit"
            disabled={!isFormValid || isLoading}
            className="w-full bg-gray-900 text-white py-5 rounded-2xl font-black shadow-xl disabled:opacity-20 disabled:grayscale flex items-center justify-center gap-3 uppercase tracking-widest text-sm mt-4"
            whileHover={isFormValid ? { scale: 1.02, backgroundColor: '#000' } : {}}
            whileTap={isFormValid ? { scale: 0.98 } : {}}
          >
            {isLoading ? (
              <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Create Account <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </>
            )}
          </motion.button>
        </form>

        <p className="text-center mt-8 text-xs font-bold text-gray-400">
          Already have an account?{' '}
          <button 
            onClick={onSwitchToLogin}
            className="text-blue-600 hover:text-blue-700 underline underline-offset-4"
          >
            Sign In
          </button>
        </p>
      </div>
    </motion.div>
  );
}

function SocialButton({ icon, label, onClick, color }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-center gap-3 py-4 px-6 border-2 border-gray-100 rounded-2xl font-black text-sm uppercase tracking-widest transition-all ${color}`}
    >
      <SafeIcon icon={icon} className="w-5 h-5" />
      {label}
    </button>
  );
}